import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef, OnChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Evse } from '@kempoweroyj/ngx-evse-api-client';
import { AuthService } from 'providers/auth.service';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, startWith, switchMap, tap } from 'rxjs/operators';
import { isOccupied } from 'shared/connector-status-pipes/connector-status.pipe';
import { Satellite, StationsService } from 'shared/stations-service/stations.service';
import { VehiclesService } from 'shared/stations-service/vehicles.service';
import * as _ from 'lodash';

@Component({
    selector: 'ce-satellites',
    templateUrl: './satellites.component.html',
    styleUrls: ['./satellites.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class SatellitesComponent implements OnInit, OnChanges {
    @Input() satellites: Satellite[];
    @Input() satelliteStyle: { [satId: number]: any };
    @Input() disabled = false;
    @Input() wrap = false;
    @Input() displayStationName = false;
    @Input() displayTxInline = true;
    @Input() mapMaximized = false;
    @Input() isHistoric = false;
    @Input() showSatellites: boolean;

    updateEvDisplayNames$ = new Subject<void>();
    evDisplayNameByConnector: { [stationIdPlusEvseId: string]: string } = {};
    /** This is used to not reserve space for the priority, if no priority is set */
    prioritySetForAnyConnector: boolean;

    // i18n texts
    connectorText = $localize`:Charger map|:Connector`;
    vehicleScheduledDepartureText = $localize`:Plugged in vehicle scheduled|Vehicle has a set session of charge and is ready to depart at certain time:Vehicle scheduled for departure`;

    constructor(
        private vehicles: VehiclesService,
        private cdRef: ChangeDetectorRef,
        public stations: StationsService,
        private authService: AuthService
    ) {}

    ngOnInit(): void {
        const updateRequested$ = this.updateEvDisplayNames$.pipe(startWith([]));
        const hasEvAccess$ = this.authService.hasAccessTo$('ev.access').pipe(startWith(false), distinctUntilChanged());

        combineLatest([hasEvAccess$, updateRequested$])
            .pipe(
                filter(([access]) => !!access),
                switchMap(() =>
                    this.vehicles.vehicles$().pipe(
                        tap((vehicles) => {
                            this.evDisplayNameByConnector = {};
                            const satellites = this.satellites || [];

                            // Don't reset the prioritySetForAnyConnector if we are showing historic data to prevent connectors from "jumping around"
                            if (this.prioritySetForAnyConnector && !this.isHistoric) {
                                this.prioritySetForAnyConnector = false;
                            }

                            for (const sat of satellites) {
                                for (const conn of sat.connectors) {
                                    if (this.isHistoric) {
                                        // When checking historic we need to check what priority was given to the session. We don't have historic data of connector
                                        conn.chargingPriority =
                                            conn?.tx?.rawSession?.tx?.transactionData?.rawSession?.session?.session?.chargingPriority;
                                    }

                                    if (conn.chargingPriority) {
                                        this.prioritySetForAnyConnector = true;
                                    }

                                    if (isOccupied(conn)) {
                                        const vehicle = vehicles.find((v) =>
                                            v.chargingPorts.some(
                                                (cp) =>
                                                    cp.macAddress.toLowerCase() === conn.tx.evMacAddress?.toLowerCase()
                                            )
                                        );
                                        this.evDisplayNameByConnector[conn.stationId + conn.evseId] =
                                            vehicle?.name || vehicle?.evModel || conn.tx.evIdentify?.displayName;
                                    }
                                }
                            }
                            this.cdRef.markForCheck();
                        })
                    )
                ),
                untilDestroyed(this)
            )
            .subscribe();
    }

    ngOnChanges() {
        this.updateEvDisplayNames$.next();
    }

    trackBySatellite(idx: number, satellite: Satellite) {
        return satellite.satelliteId;
    }

    trackByConnector(idx: number, connector: Evse) {
        return connector.evseId;
    }

    setActiveTx(connector: Evse) {
        this.stations.highlightedEvse$.next(
            connector ? { stationId: connector.stationId, evseId: connector.evseId } : undefined
        );
    }

    compareSubjectToConnector(subject$, connector) {
        return _.isEqual(subject$, connector);
    }

    setHighlightTx(connector: Evse) {
        this.stations.highlightedTxId$.next(connector?.tx?.txId);
    }

    getChargingPriorityToolTip(chargingPriority: number) {
        if (chargingPriority !== 0 && chargingPriority != null) {
            const priorityString =
                chargingPriority > 0 ? $localize`:Charging priority|:High` : $localize`:Charging priority|:Low`;
            return $localize`:Charging priority tooltip|:The connector has ${priorityString} charging priority`;
        } else {
            return '';
        }
    }
}
